import type { BasePageProps } from '@shared/components/BasePageProps';
import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { Reaction } from '@shared/domain/reaction/Reaction';
import type { CurrentReadableReview, ReviewSection } from './types/types';
import { Alert, Box, Button, Typography } from '@mui/material';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import SEO from '@/shared/components/SEO';
import CustomSnackbar from '@shared/components/Snackbar';
import { ReviewStatusChip } from '../../components/ReviewStatusChip';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import {

  ReactionSubjectType,
  ReactionType,
} from '@shared/domain/reaction/Reaction';
import { ReviewStatus, ReviewStep } from '@shared/domain/review';
import { useResponsive } from '@shared/hooks/useResponsive';
import { useApi } from '@shared/services/api';
import { theme } from '@shared/theme';
import SignUpOrLogInModal from '../authentication/components/SignUpOrLogInModal';
import ReviewUnlockModal from '../review/components/DeepDiveReviewCountModal';
import { useReviewContext } from '../review/context/ReviewContext';
import { getReviewStepName } from '../review/helpers/ReviewStepNames';
import AdminLegalFlagsPanel from './components/AdminLegalFlagsPanel';
import BasicReviewReader from './BasicReviewReader';
import QAPanel from './components/QAPanel';
import ReactionInputs from './components/ReactionInputs';
import ReviewBanner from './components/ReviewHeader';
import { formatReviewData } from './helpers/FormatReadableReview';
import TrialExpiredPaywall from '../company/company-profile-page/components/TrialExpiredPaywall';
import ReviewPlaceholder from './ReviewPlaceholder';

interface ReviewReaderProps extends BasePageProps {
  reviewId?: number;
  name?: string;
}

const loadingMessages = [
  'Gathering absolute premium review data...',
  'Sifting through corporate nonsense...',
  'Translating legalese into something human-readable...',
  'Asking the database nicely to hurry up...',
  'Fetching insights… or at least trying to...',
  'Extracting wisdom from the void...',
  'Loading... because instant gratification is a myth.',
  'Prying open the vault of review secrets...',
  'Compiling expert opinions (or just opinions)...',
  'Waking up the servers, they were napping.',
  'Decrypting sales jargon for your convenience...',
  'Patching together words that sound smart...',
  'Negotiating with the API for faster responses...',
  'Fine-tuning insights with AI magic...',
  'Trying to make sense of procurement madness...',
  'Analyzing reviews with a dash of sarcasm...',
  'Extracting the good stuff, discarding the fluff...',
  'Rewiring the internet just for this review...',
  'Consulting the data oracles, please hold...',
  'Bribing the server hamsters for speed...',
];

function getRandomLoadingMessage() {
  return loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
}

function ReviewReader(props: ReviewReaderProps) {
  const justAuthenticated = useRef(false);
  const { reviewId: paramReviewId, name: paramName } = useParams<{
    reviewId: string;
    name?: string;
  }>();
  const reviewId = props.reviewId ?? paramReviewId;
  const name = props.name ?? paramName;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const questionIdParam
    = searchParams.get('questionid') || searchParams.get('questionId');

  const { user, userLoadState, refetchUser } = useContext(UserContext);
  const isContributor = !user?.isAdmin && user?.planSlug === 'contributor';
  const isTrialExpired = !user?.isAdmin && user?.subscriptionStatus === 'trial_expired';
  const [contributorBlocked, setContributorBlocked] = useState(false);

  const [isSticky, setIsSticky] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const originalBannerPosition = useRef<number | null>(null);
  const askQuestionHandlerRef = useRef<(() => void) | null>(null);
  const refetchQuestionsRef = useRef<(() => void) | null>(null);

  const handleAskQuestionReady = useCallback((handler: () => void) => {
    askQuestionHandlerRef.current = handler;
  }, []);

  const handleQuestionsRefetchReady = useCallback((refetch: () => void) => {
    refetchQuestionsRef.current = refetch;
  }, []);

  const handleRefetchQuestions = useCallback(() => {
    refetchQuestionsRef.current?.();
  }, []);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [editingSections, setEditingSections] = useState<Set<number>>(new Set());
  const [unlock, setUnlock] = useState<string | null>(
    searchParams.get('unlock'),
  );
  const [deepDiveReviewLimitModal, setDeepDiveReviewLimitModal]
    = useState(false);
  const [currentFormattedReview, setCurrentFormattedReview]
    = useState<CurrentReadableReview | null>(null);
  const { currentReview, setCurrentReview } = useReviewContext();
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [myReactions, setMyReactions] = useState<Reaction[]>([]);
  const [reactionCounts, setReactionCounts] = useState({
    likeDislikeCount: 0,
    likePercentage: 0,
  });

  const api = useApi();
  const isFromEmail = searchParams.get('fromEmail') === 'true';
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const questionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const scrollToSection = (key?: string) => {
    if (!key)
      return window.scrollTo({ top: 0, behavior: 'smooth' });

    const el = sectionRefs.current[key];
    if (el) {
      // Calculate offset for sticky banner when not mobile
      let offset = 0;
      if (!isMobile && isSticky && bannerRef.current) {
        offset = bannerRef.current.offsetHeight + 16; // Add some padding
      }

      const elementPosition = el.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
    else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToQuestion = (questionId: number) => {
    // First scroll to QA section
    const qaSection = sectionRefs.current.qa;
    if (qaSection) {
      let offset = 0;
      if (!isMobile && isSticky && bannerRef.current) {
        offset = bannerRef.current.offsetHeight + 16;
      }

      const elementPosition = qaSection.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });

      // Wait for scroll to complete, then scroll to specific question
      setTimeout(() => {
        const questionEl = questionRefs.current[questionId];
        if (questionEl) {
          const questionPosition = questionEl.offsetTop - offset - 100; // Extra padding for better visibility
          window.scrollTo({ top: questionPosition, behavior: 'smooth' });
        }
      }, 500);
    }
  };

  const getCurrentReview = useCallback(
    async (overrideUnlock?: string) => {
      if (userLoadState !== UserLoadState.Authenticated)
        return;
      if (isTrialExpired)
        return;
      setLoading(true);
      try {
        const response = await api.get(`review/${reviewId}/full`, {
          params: {
            unlock: overrideUnlock != null ? overrideUnlock : unlock,
          },
        });

        if (response.error) {
          const status = response.status;
          if (status === 402) {
            navigate({
              pathname: `/company/${encodeURIComponent(name!)}`,
              search: `?${createSearchParams({ planMaxed: String(reviewId) })}`,
            });
            return;
          }
          if (status === 403 && isContributor) {
            setContributorBlocked(true);
            return;
          }
          setSnackbarConfig({
            type: 'error',
            message: 'An error has occurred, please try again later.',
            open: true,
          });
          return;
        }

        setCurrentReview(response);
        const formattedData: CurrentReadableReview = formatReviewData(response);
        setDeepDiveReviewLimitModal(!!response.creditConsumeWarning);
        setCurrentFormattedReview(formattedData);

        // Set initial reaction counts
        setReactionCounts({
          likeDislikeCount: response.likeDislikeCount || 0,
          likePercentage: response.likePercentage || 0,
        });
      }
      catch {
        setSnackbarConfig({
          type: 'error',
          message: 'An error has occurred, please try again later.',
          open: true,
        });
      }
      finally {
        setLoading(false);
      }
    },
    [userLoadState, reviewId, unlock, user?.idToken, navigate, name],
  );

  const updateReviewStatus = useCallback(async (newStatus: ReviewStatus) => {
    setCurrentReview((prevReview) => {
      if (!prevReview) return prevReview;
      return {
        ...prevReview,
        status: newStatus,
      };
    });

    try {
      const response = await api.get(`review/${reviewId}/full`, {
        params: { unlock },
      });

      if (!response.error) {
        setCurrentReview(response);
        const formattedData: CurrentReadableReview = formatReviewData(response);
        setCurrentFormattedReview(formattedData);
      }
    } catch (error) {
      console.error('Error refetching review:', error);
    }
  }, [setCurrentReview, api, reviewId, unlock]);

  const toggleSectionEditMode = useCallback((sectionKey: ReviewStep) => {
    setEditingSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  }, []);

  const fetchMyReactions = async () => {
    if (
      !currentFormattedReview
      || userLoadState !== UserLoadState.Authenticated
    ) {
      return;
    }
    try {
      const result = await api.get('/reactions', {
        params: {
          subjectId: [Number(reviewId)],
          subjectType: ReactionSubjectType.Review,
          userId: user?.id,
          limit: 100,
          orderBy: 'createdAt',
          orderDirection: 'DESC',
        },
      });
      setMyReactions(result.data as Reaction[]);
    }
    catch (error) {
      console.error('Error fetching reactions:', error);
    }
  };

  useEffect(() => {
    fetchMyReactions();
  }, [currentFormattedReview]);

  useEffect(() => {
    const fetchReactionCounts = async () => {
      // Don't fetch if user isn't authenticated
      if (!user?.idToken)
        return;

      try {
        const response = await api.get('/reactions/counts', {
          params: {
            subjectId: Number(reviewId),
            subjectType: ReactionSubjectType.Review,
          },
        });

        const likeDislikeCount
          = (response.data[ReactionType.Like] || 0)
            + (response.data[ReactionType.Dislike] || 0);
        const likePercentage
          = (response.data[ReactionType.Like] || 0) / likeDislikeCount || 0;

        setReactionCounts({
          likeDislikeCount,
          likePercentage,
        });
      }
      catch (err: any) {
        console.error('Error fetching reaction counts:', err);
      }
    };

    fetchReactionCounts();
  }, [myReactions, user?.idToken]);

  useEffect(() => {
    if (!user?.idToken)
      return;
    refetchUser({
      email: user?.email || '',
      idToken: user?.idToken || '',
    });
  }, [user?.idToken]);

  useEffect(() => {
    if (userLoadState === UserLoadState.Authenticated) {
      getCurrentReview();
    }
    else if (userLoadState === UserLoadState.Unauthenticated && !user?.idToken) {
      if (justAuthenticated.current) {
        justAuthenticated.current = false;
      }
      else {
        setShowAuthModal(true);
      }
    }
  }, [userLoadState, unlock, getCurrentReview]);

  // Scroll detection for sticky banner
  useEffect(() => {
    if (isMobile)
      return;

    const handleScroll = () => {
      if (bannerRef.current) {
        // Store the original position when not sticky
        if (!isSticky && originalBannerPosition.current === null) {
          originalBannerPosition.current
            = bannerRef.current.offsetTop + bannerRef.current.offsetHeight;
        }

        // Use stored position for calculation
        const triggerPoint
          = originalBannerPosition.current
            || bannerRef.current.offsetTop + bannerRef.current.offsetHeight;

        const shouldStick = window.scrollY > triggerPoint - 100;
        setIsSticky(shouldStick);
      }
    };

    // Set initial position
    if (bannerRef.current && originalBannerPosition.current === null) {
      originalBannerPosition.current
        = bannerRef.current.offsetTop + bannerRef.current.offsetHeight;
    }

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, isSticky]);

  // Reset position when content changes
  useEffect(() => {
    originalBannerPosition.current = null;
    setIsSticky(false);
  }, [currentFormattedReview]);

  // Handle scrolling to question from email link
  useEffect(() => {
    if (questionIdParam && currentFormattedReview && !loading) {
      // Wait a bit for the page to fully render
      const timeoutId = setTimeout(() => {
        scrollToQuestion(Number(questionIdParam));
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [questionIdParam, currentFormattedReview, loading]);

  const handleReject = async () => {
    try {
      const response = await api.post(`/review/${reviewId}/edit`, {
        status: ReviewStatus.REJECTED,
      });
      if (response.success === true) {
        setSnackbarConfig({
          type: 'success',
          message: 'Review rejected successfully.',
          open: true,
        });
        // Refresh the review data to reflect the new status
        await getCurrentReview();
      }
    }
    catch (error) {
      // Handle error (e.g., show an error message)
      console.error('Error rejecting review:', error);
    }
  };

  const handleApprove = async () => {
    try {
      const response = await api.post(`/review/${reviewId}/approve`);
      if (response.success === true) {
        setSnackbarConfig({
          type: 'success',
          message: 'Review approved successfully.',
          open: true,
        });
        // Refresh the review data to reflect the new status
        await getCurrentReview();
      }
    }
    catch (error) {
      // Handle error (e.g., show an error message)
      console.error('Error approving review:', error);
    }
  };

  const sections = useMemo(() => {
    // Check if user can edit this review
    const isOwnReview = user?.id === currentReview?.userId;
    const editableStatuses = [ReviewStatus.IN_PROGRESS, ReviewStatus.NEEDS_EDITING];
    const canEditReview = !!user?.isAdmin || (isOwnReview && editableStatuses.includes(currentReview?.status as ReviewStatus));

    // Check if RFP is relevant based on basic review
    const hasRfp = currentFormattedReview?.reviewBasic?.dealRfp?.answer === 'Yes';

    // If review is editable, show all sections (even empty ones) so user knows what to complete
    // If not editable, only show sections with content
    const allSections = [
      {
        section: currentFormattedReview?.reviewBasic,
        title: getReviewStepName(ReviewStep.BASIC_REVIEW),
        key: ReviewStep.BASIC_REVIEW,
        canEdit: canEditReview,
      },
      canEditReview || (currentFormattedReview?.reviewEntryStrategy
      && Object.keys(currentFormattedReview?.reviewEntryStrategy || {}).length > 0) ? {
        section: currentFormattedReview?.reviewEntryStrategy || (canEditReview ? {} : undefined),
        title: getReviewStepName(ReviewStep.ENTRY_STRATEGY),
        key: ReviewStep.ENTRY_STRATEGY,
        canEdit: canEditReview,
      } : false,
      canEditReview || (currentFormattedReview?.reviewPurchaseDetails
      && Object.keys(currentFormattedReview?.reviewPurchaseDetails || {}).length
      > 0) ? {
        section: currentFormattedReview?.reviewPurchaseDetails || (canEditReview ? {} : undefined),
        title: getReviewStepName(ReviewStep.PURCHASE_DETAILS),
        key: ReviewStep.PURCHASE_DETAILS,
        canEdit: canEditReview,
      } : false,

      canEditReview || (currentFormattedReview?.reviewEvaluation
      && Object.keys(currentFormattedReview?.reviewEvaluation || {}).length > 0) ? {
        section: currentFormattedReview?.reviewEvaluation || (canEditReview ? {} : undefined),
        title: getReviewStepName(ReviewStep.EVALUATION),
        key: ReviewStep.EVALUATION,
        canEdit: canEditReview,
      } : false,
      canEditReview || (currentFormattedReview?.reviewNegotiation
      && Object.keys(currentFormattedReview?.reviewNegotiation || {}).length > 0) ? {
        section: currentFormattedReview?.reviewNegotiation || (canEditReview ? {} : undefined),
        title: getReviewStepName(ReviewStep.NEGOTIATION),
        key: ReviewStep.NEGOTIATION,
        canEdit: canEditReview,
      } : false,
      canEditReview || (currentFormattedReview?.reviewLegal
      && Object.keys(currentFormattedReview?.reviewLegal || {}).length > 0) ? {
        section: currentFormattedReview?.reviewLegal || (canEditReview ? {} : undefined),
        title: getReviewStepName(ReviewStep.LEGAL),
        key: ReviewStep.LEGAL,
        canEdit: canEditReview,
      } : false,
      // Only show RFP section if dealRfp was answered "Yes" in basic review
      hasRfp && (canEditReview || (currentFormattedReview?.reviewRfp
      && Object.keys(currentFormattedReview?.reviewRfp || {}).length > 0)) ? {
        section: currentFormattedReview?.reviewRfp || (canEditReview ? {} : undefined),
        title: getReviewStepName(ReviewStep.RFP),
        key: ReviewStep.RFP,
        canEdit: canEditReview,
      } : false,
    ];

    return allSections.filter(
      (
        item,
      ): item is { section: ReviewSection; title: string; key: ReviewStep; canEdit: boolean } =>
        !!item,
    );
  }, [currentFormattedReview, currentReview, user]);

  useEffect(() => {
    if (isFromEmail) {
      sessionStorage.setItem('reviewId', String(reviewId) || '');
      sessionStorage.setItem('companyName', name || '');
    }
  }, [isFromEmail]);

  const seoTitle = currentFormattedReview?.companyName
    ? `${currentFormattedReview.companyName} Review`
    : 'Company Review';

  const seoDescription = currentFormattedReview?.companyName
    ? `Read detailed sales insights about ${currentFormattedReview.companyName}.  Get valuable, deal-closing intel about procurement, evaluation processes, legal review, security review, RFPs, economic buyers, and other key buying factors.`
    : 'Read detailed sales insights and company reviews to improve your sales outcomes.';

  if (loading) {
    return <LoadingSpinner variant="circular" size="large" message={getRandomLoadingMessage()} fullScreen />;
  }

  if (isTrialExpired || contributorBlocked) {
    const displayName = decodeURIComponent(name || '');
    return (
      <>
        <SEO
          title={displayName ? `${displayName} Review` : 'Company Review'}
          description="Subscribe to Kandir Pro to read this review."
          ogImage="https://app.kandir.io/kandir_logo.png"
          twitterImage="https://app.kandir.io/kandir_logo.png"
        />
        <ReviewBanner
          ref={bannerRef}
          companyName={displayName}
          companyId={0}
          setIsEditMode={() => {}}
          isEditMode={false}
          reviewDate=""
          onScrollToSection={() => {}}
          sections={[]}
          reviewId={Number(reviewId)}
          reactionCounts={{ likeDislikeCount: 0, likePercentage: 0 }}
          isSticky={false}
          onAskQuestionReady={() => {}}
          refetchQuestions={() => {}}
        />
        <TrialExpiredPaywall standalone companyName={displayName} isContributor={isContributor} />
      </>
    );
  }

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        ogImage="https://app.kandir.io/kandir_logo.png"
        twitterImage="https://app.kandir.io/kandir_logo.png"
      />
      {/*
        AI-scanner observations for admins. Rendered ABOVE the moderation
        Alert so a reviewer sees what the scanner thought before they hit
        Approve / Reject. Component decides for itself whether to render
        anything; non-admins get a 403 from the endpoint and see nothing.
      */}
      {user?.isAdmin && reviewId && (
        <AdminLegalFlagsPanel reviewId={Number(reviewId)} />
      )}

      {currentReview && currentReview.status !== ReviewStatus.COMPLETED && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ m: 3, display: 'flex', alignItems: 'center' }}
        >
          <Box display="flex" alignItems="center">
            <Box>
              <Typography variant="body1" component="div">
                This review is
                {' '}
                <ReviewStatusChip status={currentReview.status} />
              </Typography>
            </Box>
            {currentReview.status === ReviewStatus.PENDING_APPROVAL
              && user?.isAdmin && (
              <Box ml={3} display="flex" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                  }}
                  onClick={handleApprove}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    color: 'white',
                  }}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </Box>
            )}
            {currentReview.status === ReviewStatus.IN_PROGRESS
              && user?.isAdmin && (
              <Box ml={3} display="flex" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                  }}
                  onClick={handleApprove}
                >
                  Complete & Approve
                </Button>
              </Box>
            )}
          </Box>
        </Alert>
      )}
      <ReviewBanner
        ref={bannerRef}
        companyName={currentFormattedReview?.companyName || ''}
        companyId={currentFormattedReview?.companyId || 0}
        setIsEditMode={(editing) => {
          if (editing) {
            const allEditableSections = sections
              .filter(s => s.canEdit)
              .map(s => s.key);
            setEditingSections(new Set(allEditableSections));
          } else {
            setEditingSections(new Set());
          }
        }}
        isEditMode={editingSections.size > 0}
        reviewDate={moment(currentFormattedReview?.createdAt).format('ll')}
        onScrollToSection={scrollToSection}
        sections={sections.map(({ key, title }) => ({
          key: String(key),
          title,
        }))}
        reviewId={Number(reviewId)}
        reactionCounts={reactionCounts}
        isSticky={isSticky}
        onAskQuestionReady={handleAskQuestionReady}
        refetchQuestions={handleRefetchQuestions}
      />

      {/* Placeholder to prevent content jump when banner becomes fixed */}
      {isSticky && !isMobile && (
        <Box
          sx={{
            height: (bannerRef.current?.offsetHeight || 0) + 22,
          }}
        />
      )}

      <ReviewUnlockModal
        open={deepDiveReviewLimitModal}
        onCancel={() => setDeepDiveReviewLimitModal(false)}
        maxConsumableDeepDivesInSubPeriod={
          user?.maxConsumableDeepDivesInSubPeriod ?? null
        }
        consumedDeepDives={user?.deepDivesViewedInSubPeriod ?? 0}
        onProceed={async () => {
          setUnlock('true');
          setDeepDiveReviewLimitModal(false);
          await getCurrentReview('true');
        }}
        cancelText="Not Now"
      />

      {userLoadState === UserLoadState.Unauthenticated && <ReviewPlaceholder />}

      {sections.map(({ key, title, section, canEdit }) =>
        section || canEdit
          ? (
              <div
                key={key}
                ref={(el) => {
                  if (el)
                    sectionRefs.current[key] = el;
                }}
              >
                <BasicReviewReader
                  section={section || {}}
                  sectionTitle={title}
                  sectionKey={key}
                  scrollToTop={scrollToSection}
                  setIsEditMode={(editing) => {
                    if (editing && !editingSections.has(key)) {
                      toggleSectionEditMode(key);
                    } else if (!editing && editingSections.has(key)) {
                      toggleSectionEditMode(key);
                    }
                  }}
                  isEditMode={editingSections.has(key)}
                  canEdit={canEdit}
                  defaultReview={currentFormattedReview}
                  updateReviewStatus={updateReviewStatus}
                />
              </div>
            )
          : null,
      )}

      {editingSections.size === 0 && reviewId && (
        <div
          ref={(el) => {
            if (el)
              sectionRefs.current.qa = el;
          }}
        >
          <Box
            sx={{
              m: '0 var(--spacing-md) var(--spacing-md) var(--spacing-md) ',
            }}
          >
            <QAPanel
              reviewId={Number(reviewId)}
              onAskQuestion={askQuestionHandlerRef.current || undefined}
              onRefetchReady={handleQuestionsRefetchReady}
              questionRefs={questionRefs}
              highlightQuestionId={
                questionIdParam ? Number(questionIdParam) : undefined
              }
            />
          </Box>
        </div>
      )}

      {editingSections.size === 0 && (
        <Box
          sx={{ m: '0 var(--spacing-md) var(--spacing-md) var(--spacing-md) ' }}
        >
          <ReactionInputs
            reviewId={reviewId!}
            reactions={myReactions}
            onReactionUpdate={async () => {
              await fetchMyReactions();
            }}
            onSnackbarUpdate={setSnackbarConfig}
          />
        </Box>
      )}

      {snackbarConfig && (
        <CustomSnackbar
          config={snackbarConfig}
          setOpen={() =>
            setSnackbarConfig(prev =>
              prev ? { ...prev, open: false } : null,
            )}
        />
      )}

      <SignUpOrLogInModal
        open={showAuthModal}
        disableClickOutside
        onClose={() => setShowAuthModal(false)}
        contextMessage="Create an account to read this deal retro"
        initialMode="login"
        handleAuthSuccess={async () => {
          justAuthenticated.current = true;
          setShowAuthModal(false);
          await refetchUser();
          await getCurrentReview();
        }}
      />
    </>
  );
}

export default ReviewReader;
